import { useMatomo } from '@mitresthen/matomo-tracker-react';
import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import type {
	CmsExternalServiceLink,
	CmsExternalServiceLinkParameter,
} from '@gov-nx/api';
import type { Nullable } from '@gov-nx/core/types';
import { propEq } from '@gov-nx/core/types';
import type { LinkParametersFormContextTypes } from './context.types';
import { useCustomEnumValues } from './hooks/useCustomEnumValues';
import type { ExternalLinkOnSubmitProps } from './types';
import {
	getFormAction,
	getFormMethod,
	isValidParameterInputKeyValue,
} from './utils';

const Context = createContext<Nullable<LinkParametersFormContextTypes>>(null);

export const LinkParametersFormContextProvider = ({
	children,
	link,
	parameters,
}: PropsWithChildren<{
	link: CmsExternalServiceLink;
	parameters: CmsExternalServiceLinkParameter[];
}>) => {
	const { customEnumValues, customEnumValuesKeys } = useCustomEnumValues();
	const { trackEvent } = useMatomo();

	const parametersInputsKeyValues = parameters
		.filter(propEq('method', 'POST'))
		.map((param) => {
			if (param.enumValue) {
				const resolvedKey = customEnumValuesKeys.find((key) =>
					param.enumValue?.includes(key)
				);

				if (resolvedKey) {
					return {
						name: param.name,
						value:
							customEnumValues[resolvedKey as keyof typeof customEnumValues],
					};
				}
			}
			return {
				name: param.name,
				value: param.value,
			};
		})
		.filter(isValidParameterInputKeyValue);

	const handleSubmit = (props: ExternalLinkOnSubmitProps) => trackEvent(props);

	return (
		<Context.Provider
			value={{
				link,
				method: getFormMethod(parameters),
				action: getFormAction(link),
				parametersInputsKeyValues,
				onSubmit: () =>
					handleSubmit({
						category: 'Partnerské portály',
						action: 'Odchod',
						name: link.link,
					}),
			}}>
			{children}
		</Context.Provider>
	);
};

export const useLinkParametersFormContext =
	(): LinkParametersFormContextTypes =>
		useContext(Context) as LinkParametersFormContextTypes;
